"use client";

import { ParticipantLiveGame, RawGame } from "@/lib/examples";
import Image from "next/image";
import { useEffect, useMemo, useState } from "react";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { getLiveGame } from "@/lib/matches/livegame";
import { RegionBadge } from "./RegionBadge";
import { buildChampUri, buildPerkUri, buildSpellUri } from "@/lib/imagecdn";
import { ExpandableContainer } from "../animated-collapsible";
import { cn } from "@/lib/utils";
import { Clock } from "lucide-react";
import { ChallengePlayerBadge } from "./ChallengePlayerBadge";

export function LiveGameCardWrapper() {
  const [queryClient] = useState(() => new QueryClient());

  return (
    <div>
      <QueryClientProvider client={queryClient}>
        <LiveGameCardQueryWrapper />
      </QueryClientProvider>
    </div>
  );
}

export function LiveGameCardQueryWrapper() {
  const query = useQuery({
    queryKey: ["livegame"],
    queryFn: getLiveGame,
    refetchInterval: 120000,
  });
  const response = query.data;
  const game = response?.game?.game;
  const isPlaying = Boolean(game && response?.playing);

  return (
    <>
      {isPlaying && game && (
        <ExpandableContainer
          defaultOpen={true}
          title={
            <div className="flex flex-row justify-center items-center gap-4">
              <h2 className="text-3xl sm:text-4xl font-black bg-gradient-to-r from-streamer-purple via-[#C084FC] to-noway-mint bg-clip-text text-transparent">
                Live Game
              </h2>
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-rose-500/20 border border-rose-500/50 shadow-[0_0_15px_rgba(244,63,94,0.4)] animate-pulse">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-500" />
                </span>
                <span className="text-xs font-black tracking-widest text-rose-400 uppercase">
                  LIVE IN-GAME
                </span>
              </div>
            </div>
          }
        >
          <LiveGameCard game={game} region={response?.game?.region} />
        </ExpandableContainer>
      )}
    </>
  );
}

export function LiveGameCard({
  game,
  region,
}: {
  game: RawGame;
  region?: string;
}) {
  const participants = game.participants ?? [];

  const blueTeamParticipants = participants.filter(
    (participant) => participant.teamId === 100
  );
  const redTeamParticipants = participants.filter(
    (participant) => participant.teamId === 200
  );
  const bannedChampions = game.bannedChampions ?? [];

  const blueTeamBans = bannedChampions.filter((ban) => ban.teamId === 100);
  const redTeamBans = bannedChampions.filter((ban) => ban.teamId === 200);

  const gameStart = game.gameStartTime || Date.now();
  const now = Date.now();
  const gameLengthInSeconds = Math.max(0, Math.floor((now - gameStart) / 1000));

  const redTeam = {
    participants: redTeamParticipants,
    bans: redTeamBans,
  };
  const blueTeam = {
    participants: blueTeamParticipants,
    bans: blueTeamBans,
  };

  const [timeSeconds, setTimeSeconds] = useState(gameLengthInSeconds);
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeSeconds((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const minutes = Math.floor(timeSeconds / 60)
    .toString()
    .padStart(2, "0");
  const seconds = (timeSeconds % 60).toString().padStart(2, "0");

  return (
    <div className="relative rounded-2xl bg-[#0C1220]/90 backdrop-blur-xl border border-white/10 shadow-2xl overflow-hidden group/live">
      {/* Top Neon Border Line */}
      <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-streamer-purple via-rose-500 to-noway-mint z-20" />

      {/* Header Bar */}
      <div className="p-4 sm:p-5 border-b border-white/10 flex flex-wrap items-center justify-between gap-4 bg-black/20">
        <div className="flex items-center gap-3">
          {/* Neon Pulsing Live Badge */}
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-rose-500/20 border border-rose-500/50 shadow-[0_0_15px_rgba(244,63,94,0.4)]">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-500 shadow-sm" />
            </span>
            <span className="text-xs font-black tracking-widest text-rose-400 uppercase">
              LIVE IM SPIEL
            </span>
          </div>

          <span className="text-xs font-mono uppercase tracking-wider text-white/40 hidden sm:inline-block">
            Offizieller Stream-Broadcast
          </span>
        </div>

        {/* Right Controls: Animated Live Timer & Region */}
        <div className="flex items-center gap-3">
          {/* Animated Live Timer */}
          <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-black/40 border border-white/10 font-mono text-xs font-bold text-white shadow-inner">
            <Clock className="w-3.5 h-3.5 text-noway-mint animate-pulse" />
            <span className="text-white/50 uppercase tracking-wider font-semibold hidden xs:inline">
              Spielzeit:
            </span>
            <span
              className="text-noway-mint font-black tabular-nums tracking-wide text-sm"
              suppressHydrationWarning
            >
              {minutes}:{seconds}
            </span>
          </div>

          <RegionBadge region={region} longName />
        </div>
      </div>

      {/* Team Boards */}
      <div className="p-4 sm:p-6">
        <MemorizedLiveGameCard redTeam={redTeam} blueTeam={blueTeam} />
      </div>
    </div>
  );
}

export type LiveGameTeamsOverviewProps = {
  redTeam: {
    participants: ParticipantLiveGame[];
    bans: { championId: number }[];
  };
  blueTeam: {
    participants: ParticipantLiveGame[];
    bans: { championId: number }[];
  };
};

export function LiveGameTeamsOverview({
  redTeam,
  blueTeam,
}: LiveGameTeamsOverviewProps) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <LiveGameTeamView team={blueTeam} color="blue" />
      <LiveGameTeamView team={redTeam} color="red" />
    </div>
  );
}

export function LiveGameTeamView({
  team,
  color,
}: {
  team: {
    participants: ParticipantLiveGame[];
    bans: { championId: number }[];
  };
  color: "red" | "blue";
}) {
  const isBlue = color === "blue";
  const teamName = isBlue ? "Blaues Team" : "Rotes Team";

  return (
    <div
      className={cn(
        "rounded-2xl p-4 sm:p-5 border backdrop-blur-md flex flex-col gap-4 transition-all duration-300",
        isBlue
          ? "border-blue-500/30 bg-[#081224]/85 shadow-[0_0_25px_rgba(59,130,246,0.12)] hover:border-blue-500/50"
          : "border-rose-500/30 bg-[#1A0C16]/85 shadow-[0_0_25px_rgba(244,63,94,0.12)] hover:border-rose-500/50"
      )}
    >
      {/* Team Header & Bans */}
      <div
        className={cn(
          "flex items-center justify-between pb-3 border-b",
          isBlue ? "border-blue-500/20" : "border-rose-500/20"
        )}
      >
        <div className="flex items-center gap-2.5">
          <span
            className={cn(
              "w-2.5 h-2.5 rounded-full",
              isBlue
                ? "bg-blue-500 shadow-[0_0_8px_#3b82f6]"
                : "bg-rose-500 shadow-[0_0_8px_#f43f5e]"
            )}
          />
          <h3
            className={cn(
              "text-sm font-black uppercase tracking-wider",
              isBlue ? "text-blue-400" : "text-rose-400"
            )}
          >
            {teamName}
          </h3>
        </div>

        {/* Bans Strip */}
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-mono uppercase text-white/40 mr-1 hidden sm:inline">
            Banns:
          </span>
          {team.bans.map((ban, idx) => (
            <BannedChampion
              key={`${color}-ban-${ban.championId}-${idx}`}
              championId={ban.championId}
            />
          ))}
        </div>
      </div>

      {/* Players List */}
      <div className="flex flex-col gap-2">
        {team.participants.map((participant, idx) => (
          <Participant
            key={participant.riotId || idx}
            participant={participant}
            color={color}
          />
        ))}
      </div>
    </div>
  );
}

export function MemorizedLiveGameCard({
  redTeam,
  blueTeam,
}: LiveGameTeamsOverviewProps) {
  return useMemo(
    () => <LiveGameTeamsOverview redTeam={redTeam} blueTeam={blueTeam} />,
    [redTeam, blueTeam]
  );
}

export function BannedChampion({ championId }: { championId: number }) {
  if (!championId || championId === -1) {
    return (
      <div className="w-7 h-7 rounded-lg bg-black/40 border border-white/10 flex items-center justify-center text-white/20 text-[10px] font-mono">
        -
      </div>
    );
  }

  return (
    <div className="relative group/ban">
      <Image
        src={buildChampUri(championId, "32x")}
        alt={`Ban ${championId}`}
        width={28}
        height={28}
        className="w-7 h-7 rounded-lg border border-white/20 grayscale contrast-125 object-cover shadow-sm group-hover/ban:grayscale-0 transition-all"
      />
      <div className="absolute inset-0 rounded-lg bg-red-950/30 pointer-events-none" />
    </div>
  );
}

export function Participant({
  participant,
  color,
}: {
  participant: ParticipantLiveGame;
  color: "red" | "blue";
}) {
  const isBlue = color === "blue";
  const playerName =
    participant.riotId || (participant as any).summonerName || "Summoner";
  const isNoway =
    playerName.toLowerCase().includes("noway") ||
    Boolean((participant as any).isMainPlayer);

  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 p-2.5 rounded-xl border transition-all duration-200",
        isNoway
          ? "bg-streamer-purple/20 border-streamer-purple/60 shadow-[0_0_15px_rgba(145,70,255,0.25)] border-l-4 border-l-streamer-purple"
          : participant.isKnownPlayer === true
          ? "bg-amber-300/[0.06] hover:bg-amber-300/10 border-amber-300/25 border-l-2 border-l-amber-300/70"
          : isBlue
          ? "bg-black/30 hover:bg-black/50 border-white/5 hover:border-blue-500/40 border-l-2 border-l-blue-500/60"
          : "bg-black/30 hover:bg-black/50 border-white/5 hover:border-rose-500/40 border-l-2 border-l-rose-500/60"
      )}
    >
      {/* Left: Champion + Spells + Runes */}
      <div className="flex items-center gap-2.5 min-w-0">
        {/* Champion Icon */}
        <div className="relative shrink-0">
          <Image
            src={buildChampUri(participant.championId, "32x")}
            alt={participant.championId?.toString() || "champion"}
            width={36}
            height={36}
            className="w-9 h-9 rounded-xl border border-white/20 object-cover shadow-sm"
          />
        </div>

        {/* Summoner Spells */}
        <div className="flex flex-col gap-1 shrink-0">
          {participant.spell1Id ? (
            <SpellIcon perkId={participant.spell1Id} />
          ) : null}
          {participant.spell2Id ? (
            <SpellIcon perkId={participant.spell2Id} />
          ) : null}
        </div>

        {/* Runes / Perks */}
        <div className="flex flex-col gap-1 shrink-0">
          {participant.perks?.perkIds?.[0] ? (
            <PerkIcon perkId={participant.perks.perkIds[0]} />
          ) : null}
          {participant.perks?.perkSubStyle ? (
            <PerkIcon perkId={participant.perks.perkSubStyle} />
          ) : null}
        </div>

        {/* Player Name & Tag */}
        <div className="min-w-0 ml-1">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                "text-xs font-bold truncate max-w-[120px] sm:max-w-[180px]",
                isNoway ? "text-white font-extrabold" : "text-white/90"
              )}
              title={playerName}
            >
              {playerName}
            </span>
            {isNoway && (
              <span className="shrink-0 px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider bg-streamer-purple text-white shadow-[0_0_8px_rgba(145,70,255,0.6)]">
                NOWAY
              </span>
            )}
          </div>
          <ChallengePlayerBadge player={participant} />
        </div>
      </div>
    </div>
  );
}

export function PerkIcon({ perkId }: { perkId: number }) {
  return (
    <Image
      src={buildPerkUri(perkId, "32x")}
      alt={perkId.toString()}
      className="rounded bg-black/60 border border-white/10 object-cover"
      width={16}
      height={16}
    />
  );
}

export function SpellIcon({ perkId }: { perkId: number }) {
  return (
    <Image
      src={buildSpellUri(perkId, "32x")}
      alt={perkId.toString()}
      width={16}
      height={16}
      className="rounded border border-white/20 object-cover"
    />
  );
}
